export type WisdomTranslationCode =
  | "kjv"
  | "nlt"
  | "lsg"
  | "bds"
  | "rvr1960"
  | "ntv"
  | "spanish"
  | "creole";

export type WisdomLanguageKey = "english" | "french" | "spanish" | "creole";

export type WisdomScriptureText = {
  reference: string;
} & Record<string, string | null | undefined>;

export type WisdomTranslationOption = {
  code: WisdomTranslationCode;
  label: string;
  languageKey: WisdomLanguageKey;
  fieldNames: readonly string[];
};

export type WisdomTranslationGroup = {
  languageKey: WisdomLanguageKey;
  defaultLabel: string;
  options: readonly WisdomTranslationOption[];
};

export const WISDOM_TRANSLATION_GROUPS: readonly WisdomTranslationGroup[] = [
  {
    languageKey: "english",
    defaultLabel: "English",
    options: [
      { code: "kjv", label: "KJV", languageKey: "english", fieldNames: ["kjv"] },
      { code: "nlt", label: "NLT", languageKey: "english", fieldNames: ["nlt"] },
    ],
  },
  {
    languageKey: "french",
    defaultLabel: "Français",
    options: [
      { code: "lsg", label: "LSG", languageKey: "french", fieldNames: ["lsg"] },
      { code: "bds", label: "BDS", languageKey: "french", fieldNames: ["bds"] },
    ],
  },
  {
    languageKey: "spanish",
    defaultLabel: "Español",
    options: [
      {
        code: "rvr1960",
        label: "RVR1960",
        languageKey: "spanish",
        fieldNames: ["rvr1960", "rv1960", "rvr"],
      },
      {
        code: "ntv",
        label: "NTV",
        languageKey: "spanish",
        fieldNames: ["ntv"],
      },
      {
        code: "spanish",
        label: "Español",
        languageKey: "spanish",
        fieldNames: ["spanish", "es"],
      },
    ],
  },
  {
    languageKey: "creole",
    defaultLabel: "Kreyol",
    options: [
      {
        code: "creole",
        label: "Kreyol",
        languageKey: "creole",
        fieldNames: ["creole", "kreyol", "haitianCreole", "ht", "hat", "hcv"],
      },
    ],
  },
];

export function getWisdomTranslationOption(
  code: WisdomTranslationCode
): WisdomTranslationOption | undefined {
  return WISDOM_TRANSLATION_GROUPS.flatMap((group) => group.options).find(
    (option) => option.code === code
  );
}

export function getWisdomTranslationText(
  scripture: WisdomScriptureText,
  code: WisdomTranslationCode
): string | null {
  const option = getWisdomTranslationOption(code);
  if (!option) return null;

  for (const fieldName of option.fieldNames) {
    const value = scripture[fieldName];
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }

  return null;
}

export function hasWisdomTranslation(
  scriptures: readonly WisdomScriptureText[],
  code: WisdomTranslationCode
): boolean {
  return scriptures.some((scripture) => getWisdomTranslationText(scripture, code));
}

export function getRenderableWisdomTranslations(
  scriptures: readonly WisdomScriptureText[]
) {
  return scriptures.flatMap((scripture, scriptureIndex) =>
    WISDOM_TRANSLATION_GROUPS.flatMap((group) =>
      group.options.flatMap((option) => {
        const text = getWisdomTranslationText(scripture, option.code);
        if (!text) return [];

        return {
          key: `${scriptureIndex}-${option.code}`,
          label: option.label,
          languageKey: group.languageKey,
          languageLabel: group.defaultLabel,
          reference: scripture.reference,
          text,
        };
      })
    )
  );
}
